import React, { useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PasswordProvider, usePassword } from './contexts/PasswordContext';
import { ItemsProvider, useItems } from './contexts/ItemsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PinProvider } from './contexts/PinContext';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import muiTheme from './theme/muiTheme';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import DashboardNew from './components/Dashboard/DashboardNew';
import { SharedPassword } from './components/Shared';
import SecurityTransparency from './components/Security/SecurityTransparency';
import UserGuide from './components/Guide/UserGuide';
import SharedPasswordAccess from './components/Share/SharedPasswordAccess';
import SharedPasswordPage from './pages/SharedPasswordPage';
import ShareManagementPage from './pages/ShareManagementPage';
import SecureNotesPage from './pages/SecureNotesPage';
import SecureFilesPage from './pages/SecureFilesPage';
import SettingsPage from './pages/SettingsPage';
import KnowledgePage from './pages/KnowledgePage';
import { ErrorBoundary } from './components/UI';
import { AppLayout } from './components/Layout';
import PWAUpdatePrompt from './components/PWA/PWAUpdatePrompt';
import { 
  SecurityRecommendations,
  LoadingFallback
} from './components/LazyComponents.jsx';
import './utils/securityTransparency'; // 🔒 보안 투명성 정보 추가
import './App.css';
import './styles/components/settings-2fa-button-fix.css'; // 2FA 버튼 스타일 수정
import './styles/components/settings-reset-button-fix.css'; // 초기화 버튼 스타일 수정
import './styles/components/settings-modal-footer-fix.css'; // 설정 모달 Footer 수정
import './components/CreditCard/CreditCardForm.css';
import './components/ApiKey/ApiKeyForm.css';

/**
 * 공통 메뉴 액션 핸들러
 */
function useMenuHandler() {
  const [showSecurityRecommendations, setShowSecurityRecommendations] = useState(false);
  const [editingPassword, setEditingPassword] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleMenuAction = (menuId) => {
    switch (menuId) {
      case 'security':
        setShowSecurityRecommendations(true);
        break;
      default:
        break;
    }
  };

  return {
    showSecurityRecommendations,
    setShowSecurityRecommendations,
    editingPassword,
    setEditingPassword,
    showPasswordForm,
    setShowPasswordForm,
    handleMenuAction
  };
}

/**
 * 메인 앱 컴포넌트 (사이드바 모달 상태 관리)
 */
function MainApp() {
  const menuHandler = useMenuHandler();
  
  // PasswordContext에서 passwords 가져오기
  const { passwords, updatePassword } = usePassword();
  
  // ItemsContext에서 통계 정보 가져오기
  const { stats } = useItems();

  // 보안 권장사항에서 패스워드 수정 요청 처리
  const handlePasswordSecurityUpdate = (passwordId, issueType) => {
    const passwordToEdit = passwords.find(pwd => pwd.id === passwordId);
    if (passwordToEdit) {
      menuHandler.setEditingPassword(passwordToEdit);
      menuHandler.setShowPasswordForm(true);
      menuHandler.setShowSecurityRecommendations(false);
      
      // 이슈 타입에 따른 안내 메시지
      switch (issueType) {
        case 'duplicate':
          alert('💡 중복된 패스워드입니다. 고유한 패스워드로 변경해주세요.');
          break;
        case 'similar':
          alert('💡 유사한 패스워드가 있습니다. 더 독특한 패스워드로 변경해주세요.');
          break;
        case 'weak':
          alert('💡 약한 패스워드입니다. 더 강력한 패스워드로 변경해주세요.');
          break;
        case 'improve':
          alert('💡 보안을 강화하기 위해 패스워드를 개선해보세요.');
          break;
        default:
          alert('💡 패스워드 보안을 개선해보세요.');
      }
    }
  };

  return (
    <>
      <AppLayout onMenuAction={menuHandler.handleMenuAction} stats={stats}>
        <DashboardNew onMenuAction={menuHandler.handleMenuAction} />
      </AppLayout>

      {/* 보안 권장사항 모달 */}
      <Suspense fallback={<LoadingFallback message="보안 분석 중..." />}>
        <SecurityRecommendations
          isOpen={menuHandler.showSecurityRecommendations}
          passwords={passwords}
          onPasswordUpdate={handlePasswordSecurityUpdate}
          onClose={() => menuHandler.setShowSecurityRecommendations(false)}
        />
      </Suspense>

    </>
  );
}

/**
 * 공유받은 패스워드 페이지 컴포넌트
 */
function SharedPasswordsApp() {
  const menuHandler = useMenuHandler();
  
  return (
    <>
      <AppLayout 
        onMenuAction={menuHandler.handleMenuAction} 
        pageTitle="공유받은 패스워드"
        pageSubtitle="다른 사용자가 공유한 패스워드를 안전하게 관리하세요"
      >
        <SharedPasswordPage />
      </AppLayout>

      {/* 보안 권장사항 모달 */}
      <Suspense fallback={<LoadingFallback message="보안 분석 중..." />}>
        <SecurityRecommendations
          isOpen={menuHandler.showSecurityRecommendations}
          passwords={[]}
          onPasswordUpdate={() => {}}
          onClose={() => menuHandler.setShowSecurityRecommendations(false)}
        />
      </Suspense>

    </>
  );
}

/**
 * 공유 관리 페이지 컴포넌트
 */
function ShareManagementApp() {
  const menuHandler = useMenuHandler();
  
  return (
    <>
      <AppLayout 
        onMenuAction={menuHandler.handleMenuAction} 
        pageTitle="공유한 패스워드"
        pageSubtitle="생성한 공유 링크를 관리하고 상태를 확인하세요"
      >
        <ShareManagementPage />
      </AppLayout>

      {/* 보안 권장사항 모달 */}
      <Suspense fallback={<LoadingFallback message="보안 분석 중..." />}>
        <SecurityRecommendations
          isOpen={menuHandler.showSecurityRecommendations}
          passwords={[]}
          onPasswordUpdate={() => {}}
          onClose={() => menuHandler.setShowSecurityRecommendations(false)}
        />
      </Suspense>

    </>
  );
}

/**
 * 보안 메모장 페이지 컴포넌트
 */
function SecureNotesApp() {
  const menuHandler = useMenuHandler();
  
  return (
    <>
      <AppLayout 
        onMenuAction={menuHandler.handleMenuAction} 
        pageTitle="보안 메모장"
        pageSubtitle="PIN으로 보호되는 안전한 메모"
      >
        <SecureNotesPage />
      </AppLayout>

      {/* 보안 권장사항 모달 */}
      <Suspense fallback={<LoadingFallback message="보안 분석 중..." />}>
        <SecurityRecommendations
          isOpen={menuHandler.showSecurityRecommendations}
          passwords={[]}
          onPasswordUpdate={() => {}}
          onClose={() => menuHandler.setShowSecurityRecommendations(false)}
        />
      </Suspense>

    </>
  );
}

/**
 * 파일 보관소 페이지 컴포넌트
 */
function SecureFilesApp() {
  const menuHandler = useMenuHandler();

  return (
    <>
      <AppLayout
        onMenuAction={menuHandler.handleMenuAction}
        pageTitle="파일 보관소"
        pageSubtitle="암호화된 파일을 안전하게 보관하세요"
      >
        <SecureFilesPage />
      </AppLayout>

      {/* 보안 권장사항 모달 */}
      <Suspense fallback={<LoadingFallback message="보안 분석 중..." />}>
        <SecurityRecommendations
          isOpen={menuHandler.showSecurityRecommendations}
          passwords={[]}
          onPasswordUpdate={() => {}}
          onClose={() => menuHandler.setShowSecurityRecommendations(false)}
        />
      </Suspense>

    </>
  );
}

/**
 * 지식 베이스 페이지 컴포넌트
 */
function KnowledgeApp() {
  const menuHandler = useMenuHandler();

  return (
    <>
      <AppLayout
        onMenuAction={menuHandler.handleMenuAction}
        pageTitle="지식 베이스"
        pageSubtitle="계정 관리 팁과 보안 정보를 저장하세요"
      >
        <KnowledgePage />
      </AppLayout>

      {/* 보안 권장사항 모달 */}
      <Suspense fallback={<LoadingFallback message="보안 분석 중..." />}>
        <SecurityRecommendations
          isOpen={menuHandler.showSecurityRecommendations}
          passwords={[]}
          onPasswordUpdate={() => {}}
          onClose={() => menuHandler.setShowSecurityRecommendations(false)}
        />
      </Suspense>

    </>
  );
}

/**
 * 루트 앱 컴포넌트
 */
function App() {
  return (
    <ErrorBoundary>
      <MuiThemeProvider theme={muiTheme}>
        <ThemeProvider>
          <AuthProvider>
            <PinProvider>
              <Router>
            <Routes>
              {/* 공유 링크 - 인증 불필요, 하지만 Firebase 접근은 필요 */}
              <Route 
                path="/shared/:shareKey" 
                element={<SharedPassword />} 
              />
              
              {/* 공유받은 패스워드 접근 - 로그인 필요 */}
              <Route 
                path="/shared-access/:token" 
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <SharedPasswordAccess />
                    </ItemsProvider>
                  </PasswordProvider>
                } 
              />
              
              {/* 공유받은 패스워드 관리 - 로그인 필요 */}
              <Route 
                path="/shared-passwords" 
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <ProtectedRoute>
                        <SharedPasswordsApp />
                      </ProtectedRoute>
                    </ItemsProvider>
                  </PasswordProvider>
                } 
              />
              
              {/* 공유 관리 페이지 - 로그인 필요 */}
              <Route 
                path="/share-management" 
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <ProtectedRoute>
                        <ShareManagementApp />
                      </ProtectedRoute>
                    </ItemsProvider>
                  </PasswordProvider>
                } 
              />
              
              {/* 보안 메모장 페이지 - 로그인 필요 */}
              <Route 
                path="/secure-notes" 
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <ProtectedRoute>
                        <SecureNotesApp />
                      </ProtectedRoute>
                    </ItemsProvider>
                  </PasswordProvider>
                } 
              />
              
              {/* 파일 보관소 페이지 - 로그인 필요 */}
              <Route 
                path="/secure-files" 
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <ProtectedRoute>
                        <SecureFilesApp />
                      </ProtectedRoute>
                    </ItemsProvider>
                  </PasswordProvider>
                } 
              />
              
              {/* 지식 베이스 페이지 - 로그인 필요 */}
              <Route
                path="/knowledge"
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <ProtectedRoute>
                        <KnowledgeApp />
                      </ProtectedRoute>
                    </ItemsProvider>
                  </PasswordProvider>
                }
              />

              {/* 설정 페이지 - 로그인 필요 */}
              <Route
                path="/settings"
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    </ItemsProvider>
                  </PasswordProvider>
                }
              />
              
              {/* 보안 투명성 페이지 - 인증 불필요, 공개 접근 */}
              <Route 
                path="/security" 
                element={<SecurityTransparency />} 
              />
              
              {/* 사용자 가이드 페이지 - 인증 불필요, 공개 접근 */}
              <Route 
                path="/guide" 
                element={<UserGuide />} 
              />
              
              {/* 메인 앱 - 인증 필요 */}
              <Route 
                path="/*" 
                element={
                  <PasswordProvider>
                    <ItemsProvider>
                      <ProtectedRoute>
                        <MainApp />
                      </ProtectedRoute>
                    </ItemsProvider>
                  </PasswordProvider>
                }
              />
            </Routes>
            </Router>
            
            {/* PWA 업데이트 프롬프트 */}
            <PWAUpdatePrompt />
          </PinProvider>
        </AuthProvider>
      </ThemeProvider>
      </MuiThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
